'use client';

import type { FC, KeyboardEvent } from 'react';

import type { TBoardLane, TBoardLaneKind } from '@/utils/auctions/auctionBoard';
import { Button, SEAM } from '@/components/ui/button/button';
import { cn } from '@/utils/cn';

/**
 * The lane switch (spec 014, research R3): the site's seam-joined pair, as a
 * tablist. Selection follows focus — Left/Right/Home/End move it — and the
 * selected half is the lit one. Both panels are already in the page, so
 * switching is instantaneous and fetches nothing.
 */

/** Phone-width labels: the pair has to share one line with room to breathe. */
const SHORT_LABEL: Record<TBoardLaneKind, string> = { sold: 'Wylicytowane', upcoming: 'Nadchodzące' };

export const laneTabId = (kind: TBoardLaneKind) => `tablica-aukcji-tab-${kind}`;
export const lanePanelId = (kind: TBoardLaneKind) => `tablica-aukcji-panel-${kind}`;

type TAuctionBoardSwitch = {
  lanes: TBoardLane[];
  selected: TBoardLaneKind;
  onSelect: (kind: TBoardLaneKind) => void;
};

export const AuctionBoardSwitch: FC<TAuctionBoardSwitch> = ({ lanes, selected, onSelect }) => {
  const kinds = lanes.map(lane => lane.kind);

  const focusTab = (kind: TBoardLaneKind) => {
    onSelect(kind);
    document.getElementById(laneTabId(kind))?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const index = kinds.indexOf(selected);
    const keys: Record<string, number> = {
      ArrowLeft: (index - 1 + kinds.length) % kinds.length,
      ArrowRight: (index + 1) % kinds.length,
      Home: 0,
      End: kinds.length - 1,
    };

    if (event.key in keys) {
      event.preventDefault();
      focusTab(kinds[keys[event.key]]);
    }
  };

  return (
    <div role="tablist" aria-label="Rodzaj aukcji" className="flex w-full items-stretch sm:w-auto">
      {lanes.map((lane, index) => {
        const isSelected = lane.kind === selected;

        return (
          <Button
            key={lane.kind}
            type="button"
            role="tab"
            id={laneTabId(lane.kind)}
            aria-selected={isSelected}
            aria-controls={lanePanelId(lane.kind)}
            tabIndex={isSelected ? 0 : -1}
            size="sm"
            variant={isSelected ? 'primary' : 'secondary'}
            onClick={() => onSelect(lane.kind)}
            onKeyDown={onKeyDown}
            className={cn('flex-1 sm:flex-none', index === 0 ? SEAM.label.prev : SEAM.label.next)}
          >
            <span className="sm:hidden">{SHORT_LABEL[lane.kind]}</span>
            <span className="hidden sm:inline">{lane.label}</span>
          </Button>
        );
      })}
    </div>
  );
};
